import {
  TestTargetUpdateInput,
  TestTargetCreateInput,
  BulkUpdateTestCasesUsecasePayload,
  BulkDeleteTestCasesUsecasePayload,
  FindIssuesInput,
  IssuePagingResponse,
  WorkspaceUser,
  WorkspaceUsersResponse,
} from "../types/api";
import { TestTargetFormData } from "../types/TestTarget";
import { format_error_message } from "../utils/errorMessage";
import { stripDeprecatedItemParamKeys } from "../utils/itemCategoryMigration";
import { mergeParamsWithClearSignal } from "../utils/mergeParams";

const normalizeDisplayOrder = (value: unknown, defaultValue = 1.0): number => {
  if (value === null || value === undefined) {
    return defaultValue;
  }

  const normalized =
    typeof value === "number" ? value : Number.parseFloat(String(value));

  return Number.isFinite(normalized) && normalized !== 0
    ? normalized
    : defaultValue;
};

// GraphQLクエリ定数
const GET_TEST_TARGETS_QUERY = `
  query GetTestTargets($getTestTargetsInput: GetTestTargetsInput!) {
    getTestTargets(getTestTargetsInput: $getTestTargetsInput) {
      name
      displayOrder
      workspaceId
      projectId
      testTargetId
      type {
        name
        typeId
      }
      description
      parentTestTargetId
      params
      value
      preparationProcedure {
        id
      }
      mainProcedure {
        id
      }
      metaInfo {
        createdAt
        createdBy
        modifiedAt
        modifiedBy
      }
    }
  }
`;

// Procedure（手順）取得・作成・更新・削除の GraphQL 定数。
// usecase→feature/procedure 統合（#884）で導入。SSTF の getProcedures は procedureIds 必須（全件取得不可）。
const PROCEDURE_FIELDS = `
  id
  workspaceId
  projectId
  name
  displayOrder
  inheritFromParent
  value
  metaInfo {
    createdAt
    modifiedAt
  }
`;

const GET_PROCEDURES_QUERY = `
  query GetProcedures($input: GetProceduresInput!) {
    getProcedures(getProceduresInput: $input) {
      ${PROCEDURE_FIELDS}
    }
  }
`;

const CREATE_PROCEDURES_MUTATION = `
  mutation CreateProcedures($input: CreateProceduresInput!) {
    createProcedures(createProceduresInput: $input) {
      ${PROCEDURE_FIELDS}
    }
  }
`;

const UPDATE_PROCEDURES_MUTATION = `
  mutation UpdateProcedures($input: UpdateProceduresInput!) {
    updateProcedures(updateProceduresInput: $input) {
      ${PROCEDURE_FIELDS}
    }
  }
`;

const DELETE_PROCEDURES_MUTATION = `
  mutation DeleteProcedures($input: DeleteProceduresInput!) {
    deleteProcedures(deleteProceduresInput: $input) {
      id
      name
    }
  }
`;

// TestTarget更新用のGraphQLミューテーション
const UPDATE_TEST_TARGETS_MUTATION = `
  mutation UpdateTestTargets($updateTestTargetsInput: UpdateTestTargetsInput!) {
    updateTestTargets(updateTestTargetsInput: $updateTestTargetsInput) {
      name
      displayOrder
      workspaceId
      projectId
      testTargetId
      type {
        typeId
        name
      }
      description
      parentTestTargetId
      params
      value
      preparationProcedure {
        id
      }
      mainProcedure {
        id
      }
      metaInfo {
        modifiedAt
        modifiedBy
      }
    }
  }
`;

// TestTarget作成用のGraphQLミューテーション
const CREATE_TEST_TARGETS_MUTATION = `
  mutation CreateTestTargets($createTestTargetsInput: CreateTestTargetsInput!) {
    createTestTargets(createTestTargetsInput: $createTestTargetsInput) {
      name
      displayOrder
      workspaceId
      projectId
      testTargetId
      type {
        typeId
        name
      }
      description
      parentTestTargetId
      params
      value
      preparationProcedure {
        id
      }
      mainProcedure {
        id
      }
      metaInfo {
        createdAt
        createdBy
        modifiedAt
        modifiedBy
      }
    }
  }
`;

// TestTarget削除用のGraphQLミューテーション
const DELETE_TEST_TARGETS_MUTATION = `
  mutation DeleteTestTargets($deleteTestTargetsInput: DeleteTestTargetsInput!) {
    deleteTestTargets(deleteTestTargetsInput: $deleteTestTargetsInput) {
      name
      displayOrder
      workspaceId
      projectId
      testTargetId
      type {
        typeId
        name
      }
      description
      parentTestTargetId
      params
      metaInfo {
        modifiedAt
        modifiedBy
      }
    }
  }
`;

// TestTargetTypes取得用のGraphQLクエリ
const GET_TEST_TARGET_TYPES_QUERY = `
  query getTestTargetTypes($getTestTargetTypesInput: GetTestTargetTypesInput!) {
    getTestTargetTypes(getTestTargetTypesInput: $getTestTargetTypesInput) {
      typeId
      name
      testTargetValueSchema
      __typename
    }
  }
`;

// APIクライアントクラス
export class SSTFApiClient {
  private sstfBaseUrl: string;
  // userToken removed - using Auth0 authentication
  private graphqlBaseUrl: string;
  private useProxy: boolean;
  private proxyUrl: string;

  constructor(
    sstfBaseUrl: string,
    // userToken removed - using Auth0 authentication
    graphqlBaseUrl?: string,
    useProxy: boolean = true
  ) {
    this.sstfBaseUrl = sstfBaseUrl;
    // userToken removed - using Auth0 authentication
    this.graphqlBaseUrl =
      graphqlBaseUrl ||
      process.env.REACT_APP_GRAPHQL_ENDPOINT ||
      "https://gql.dev.sstf.agest.app";
    this.useProxy = useProxy;
    this.proxyUrl = this.getProxyUrl();
  }

  // プロキシURLを適切に設定するメソッド
  private getProxyUrl(): string {
    if (typeof window !== "undefined") {
      return window.location.origin;
    }
    return "http://localhost";
  }

  // キャメルケースをスネークケースに変換
  private camelToSnake(obj: any): Record<string, any> {
    const converted: Record<string, any> = {};

    // スキップすべきスネークケースのキー（すでに変換済み or 重複を避けるため）
    const snakeCaseKeysToSkip = [
      "force_analyze",
      "is_download",
      "is_manual",
      "fail_count",
    ];

    for (const [key, value] of Object.entries(obj)) {
      // すでにスネークケースのキーはスキップ（重複を避ける）
      if (snakeCaseKeysToSkip.includes(key)) {
        continue;
      }

      // 特定のキーのみ変換
      if (key === "forceAnalyze") {
        converted["force_analyze"] = value;
      } else if (key === "isDownload") {
        converted["is_download"] = value;
      } else if (key === "isManual") {
        converted["is_manual"] = value;
      } else if (key === "failCount") {
        converted["fail_count"] = value;
      } else {
        converted[key] = value;
      }
    }

    return converted;
  }

  // プロキシサーバーの接続確認メソッドを追加
  async testProxyConnection(): Promise<{ success: boolean; message: string }> {
    try {
      console.log("プロキシサーバー接続確認開始:", this.proxyUrl);

      const healthUrl = `${this.proxyUrl}/health`;
      const response = await fetch(healthUrl, {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
        // タイムアウト設定
        signal: AbortSignal.timeout(10000), // 10秒
      });

      if (!response.ok) {
        throw new Error(
          `プロキシサーバーからの応答エラー: ${response.status} ${response.statusText}`
        );
      }

      const healthData = await response.json();
      console.log("プロキシサーバー接続確認成功:", healthData);

      return {
        success: true,
        message: `プロキシサーバー接続成功 (${healthData.service})`,
      };
    } catch (error) {
      console.error("プロキシサーバー接続確認失敗:", error);

      const errorMessage =
        error instanceof Error ? error.message : "不明なエラー";
      return {
        success: false,
        message: `プロキシサーバー接続失敗: ${errorMessage}`,
      };
    }
  }

  // プロキシ経由のリクエストメソッド
  private async proxyRequest<T>(
    endpoint: string,
    method: string = "GET",
    params?: Record<string, string>,
    data?: any
  ): Promise<T> {
    try {
      const response = await fetch(`${this.proxyUrl}/api/proxy`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          method,
          endpoint,
          params,
          data,
          sstf_base_url: this.sstfBaseUrl,
          // userToken removed - using Auth0 authentication
        }),
        signal: AbortSignal.timeout(30000),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Proxy request failed: ${response.status} ${response.statusText}\n${errorText}`
        );
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error(
        "プロキシリクエストエラー:",
        error instanceof Error ? error.message : error
      );
      throw error;
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    // APIのbaseURLが/api/v2を含んでいるかチェック
    const baseUrl = this.sstfBaseUrl.endsWith("/api/v2")
      ? this.sstfBaseUrl
      : `${this.sstfBaseUrl}/api/v2`;
    const url = `${baseUrl}${endpoint}`;

    console.log("HTTP Request開始:", {
      method: options.method || "GET",
      baseUrl: this.sstfBaseUrl,
      finalUrl: url,
      endpoint,
      headers: {
        "Content-Type": "application/json",
        // Auth0認証を使用するため、Authorizationヘッダーは不要
      },
    });

    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          // Auth0認証を使用するため、Authorizationヘッダーは不要
          ...options.headers,
        },
      });

      console.log("HTTP Response受信:", {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        url: response.url,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("HTTP Error Response:", {
          status: response.status,
          statusText: response.statusText,
          errorText,
        });
        throw new Error(
          `API request failed: ${response.status} ${response.statusText}\n${errorText}`
        );
      }

      const result = await response.json();
      console.log("HTTP Response JSON解析完了:", result);
      return result;
    } catch (error) {
      console.error("HTTP Request失敗:", {
        url,
        error,
        errorMessage: error instanceof Error ? error.message : "不明なエラー",
      });
      throw error;
    }
  }

  async get<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
    if (this.useProxy) {
      return this.proxyRequest<T>(endpoint, "GET", params);
    } else {
      const searchParams = params ? new URLSearchParams(params).toString() : "";
      const url = searchParams ? `${endpoint}?${searchParams}` : endpoint;
      return this.request<T>(url);
    }
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    if (this.useProxy) {
      return this.proxyRequest<T>(endpoint, "POST", undefined, data);
    } else {
      return this.request<T>(endpoint, {
        method: "POST",
        body: data ? JSON.stringify(data) : undefined,
      });
    }
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    if (this.useProxy) {
      return this.proxyRequest<T>(endpoint, "PUT", undefined, data);
    } else {
      return this.request<T>(endpoint, {
        method: "PUT",
        body: data ? JSON.stringify(data) : undefined,
      });
    }
  }

  // GraphQLリクエストメソッド（プロキシ経由）
  private async graphqlRequest<T>(
    query: string,
    variables?: Record<string, any>
  ): Promise<T> {
    try {
      const response = await fetch(`${this.proxyUrl}/api/proxy`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          method: "POST",
          endpoint: "/graphql",
          params: undefined,
          data: {
            query,
            variables,
          },
          sstf_base_url: this.graphqlBaseUrl,
          // userToken removed - using Auth0 authentication
        }),
        signal: AbortSignal.timeout(30000),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `GraphQL proxy request failed: ${response.status} ${response.statusText}\n${errorText}`
        );
      }

      const proxyResult = await response.json();
      const result = proxyResult.data;

      if (result.errors && result.errors.length > 0) {
        throw new Error(`GraphQL errors: ${JSON.stringify(result.errors)}`);
      }

      return result.data;
    } catch (error) {
      console.error(
        "GraphQLリクエストエラー:",
        error instanceof Error ? error.message : error
      );
      throw error;
    }
  }

  // ワークスペース一覧を取得
  async getWorkspaces(onlyBelonging: boolean = false): Promise<Workspace[]> {
    const params = {
      only_belonging: onlyBelonging ? "true" : "false",
    };
    return this.get<Workspace[]>("/workspaces", params);
  }

  // プロジェクト一覧を取得
  async getProjects(
    workspaceId: string,
    onlyBelonging: boolean = false
  ): Promise<Project[]> {
    const params = {
      workspace_id: workspaceId,
      only_belonging: onlyBelonging ? "true" : "false",
    };
    return this.get<Project[]>("/projects", params);
  }

  // BFF: ワークスペースユーザー複数取得（名前解決用）
  async getWorkspaceUsers(
    workspaceId: string,
    workspaceUserIds: string[]
  ): Promise<WorkspaceUser[]> {
    const ids = workspaceUserIds.filter((id): id is string => Boolean(id));
    if (ids.length === 0) {
      return [];
    }
    const params = {
      workspace_user_ids: Array.from(new Set(ids)).join(","),
    };
    const res = await this.get<WorkspaceUsersResponse>(
      `/workspaces/${workspaceId}/users`,
      params
    );
    return res?.users ?? [];
  }

  // TestTargetTypes一覧を取得（GraphQL使用）
  async getTestTargetTypes(
    workspaceId: string,
    projectId: string
  ): Promise<TestTargetType[]> {
    console.log("GraphQL経由でTestTargetTypesを取得中...", {
      workspaceId,
      projectId,
    });

    const variables = {
      getTestTargetTypesInput: {
        workspaceId,
        projectId,
      },
    };

    try {
      const result = await this.graphqlRequest<{
        getTestTargetTypes: TestTargetType[];
      }>(GET_TEST_TARGET_TYPES_QUERY, variables);

      const testTargetTypes = result.getTestTargetTypes;
      console.log("取得したTestTargetTypes:", testTargetTypes);

      return testTargetTypes;
    } catch (error) {
      console.error("TestTargetTypes取得エラー:", error);
      throw new Error(
        `TestTargetTypesの取得に失敗しました: ${
          error instanceof Error ? error.message : "不明なエラー"
        }`
      );
    }
  }

  // TestTarget一覧を取得（GraphQL使用）
  async getTestTargets(
    workspaceId: string,
    projectId: string
  ): Promise<GraphQLTestTarget[]> {
    const variables = {
      getTestTargetsInput: {
        workspaceId,
        projectId,
      },
    };

    try {
      const result = await this.graphqlRequest<{
        getTestTargets: GraphQLTestTarget[];
      }>(GET_TEST_TARGETS_QUERY, variables);

      const testTargets = result.getTestTargets;

      // displayOrderがnullの場合のデフォルト値を設定
      testTargets.forEach((target) => {
        if (target.displayOrder === null || target.displayOrder === undefined) {
          target.displayOrder = 1.0;
        } else if (
          typeof target.displayOrder === "number" &&
          target.displayOrder === 0
        ) {
          target.displayOrder = 1.0;
        } else if (typeof target.displayOrder !== "number") {
          target.displayOrder = 1.0;
        }
      });

      return testTargets;
    } catch (error) {
      console.error("TestTarget取得エラー:", error);
      throw new Error(
        `TestTargetの取得に失敗しました: ${
          error instanceof Error ? error.message : "不明なエラー"
        }`
      );
    }
  }

  // ==== Procedure（手順）CRUD（usecase→feature/procedure 統合 #884）====
  // SSTF の getProcedures は procedureIds 必須（全件取得不可）。呼び出し側でチャンク分割する。
  private static readonly PROCEDURE_FETCH_CHUNK_SIZE = 100;

  /** 手順を SSTF UUID 指定で取得する。空配列は SSTF を呼ばずに [] を返す。 */
  async getProcedures(
    workspaceId: string,
    projectId: string,
    procedureIds: string[]
  ): Promise<GraphQLProcedure[]> {
    const ids = procedureIds.filter((id): id is string => Boolean(id));
    if (ids.length === 0) return [];

    const chunkSize = SSTFApiClient.PROCEDURE_FETCH_CHUNK_SIZE;
    const results: GraphQLProcedure[] = [];
    for (let i = 0; i < ids.length; i += chunkSize) {
      const chunk = ids.slice(i, i + chunkSize);
      const variables = {
        input: { workspaceId, projectId, procedureIds: chunk },
      };
      const result = await this.graphqlRequest<{
        getProcedures: GraphQLProcedure[];
      }>(GET_PROCEDURES_QUERY, variables);
      results.push(...(result.getProcedures ?? []));
    }
    return results;
  }

  /** 手順を作成する。 */
  async createProcedures(
    workspaceId: string,
    projectId: string,
    procedures: CreateProcedureInput[]
  ): Promise<GraphQLProcedure[]> {
    const variables = {
      input: { workspaceId, projectId, procedures },
    };
    const result = await this.graphqlRequest<{
      createProcedures: GraphQLProcedure[];
    }>(CREATE_PROCEDURES_MUTATION, variables);
    return result.createProcedures ?? [];
  }

  /** 手順を更新する（SSTF の update は完全置換）。 */
  async updateProcedures(
    workspaceId: string,
    projectId: string,
    procedures: UpdateProcedureInput[]
  ): Promise<GraphQLProcedure[]> {
    const variables = {
      input: { workspaceId, projectId, procedures },
    };
    const result = await this.graphqlRequest<{
      updateProcedures: GraphQLProcedure[];
    }>(UPDATE_PROCEDURES_MUTATION, variables);
    return result.updateProcedures ?? [];
  }

  /** 手順を削除する。 */
  async deleteProcedures(
    workspaceId: string,
    projectId: string,
    procedureIds: string[]
  ): Promise<Array<{ id: string; name: string }>> {
    const ids = procedureIds.filter((id): id is string => Boolean(id));
    if (ids.length === 0) return [];
    const variables = {
      input: { workspaceId, projectId, procedureIds: ids },
    };
    const result = await this.graphqlRequest<{
      deleteProcedures: Array<{ id: string; name: string }>;
    }>(DELETE_PROCEDURES_MUTATION, variables);
    return result.deleteProcedures ?? [];
  }

  // テストラン取得
  async getTestRuns(
    workspaceId: string,
    projectId: string
  ): Promise<TestRun[]> {
    try {
      console.log("テストランを取得中...", { workspaceId, projectId });

      let allTestRuns: TestRun[] = [];
      let currentPage = 0;

      while (true) {
        const request: FindTestRunsRequest = {
          page: currentPage,
          size: 100, // 適切なサイズに調整
          workspace_id: workspaceId,
          project_id: projectId,
        };

        console.log(`ページ ${currentPage} のテストランを取得中...`);

        const response = await this.post<FindTestRunsResponse>(
          "/test_runs/find",
          request
        );

        if (
          !response.test_run_with_summaries ||
          response.test_run_with_summaries.length === 0
        ) {
          console.log(`ページ ${currentPage} のテストランはありませんでした`);
          break;
        }

        // test_run_with_summariesからTestRunオブジェクトを抽出
        const testRuns = response.test_run_with_summaries.map((summary) => ({
          test_run_id: summary.test_run.test_run_id,
          name: summary.test_run.name,
          workspace_id: summary.test_run.workspace_id,
          project_id: summary.test_run.project_id,
        }));

        allTestRuns = allTestRuns.concat(testRuns);

        // 次のページがあるかチェック
        if (!response.pagination?.has_next) {
          break;
        }

        currentPage++;
      }

      console.log(`合計 ${allTestRuns.length} 件のテストランを取得しました`);

      // 名前でソート
      allTestRuns.sort((a, b) => a.name.localeCompare(b.name));

      return allTestRuns;
    } catch (error) {
      console.error("テストラン取得エラー:", error);
      throw new Error(
        `テストランの取得に失敗しました: ${
          error instanceof Error ? error.message : "不明なエラー"
        }`
      );
    }
  }

  // テンプレート取得
  async getTemplate(
    workspaceId: string,
    projectId: string,
    templateType: string
  ): Promise<Template> {
    const params = {
      workspace_id: workspaceId,
      project_id: projectId,
    };
    return this.get<Template>(`/templates/${templateType}`, params);
  }

  // テスト結果検索（Pythonのfind_test_resultsを参考）
  async findTestResults(
    request: FindTestResultsRequest
  ): Promise<FindTestResultsResponse> {
    let allResults: ScriptTestResultDetail[] = [];
    let currentPage = 1;

    while (true) {
      const requestWithPage = {
        ...request,
        page: currentPage,
      };

      console.log(`ページ ${currentPage} のデータを取得中...`);

      const response = await this.post<FindTestResultsResponse>(
        "/test_results/find",
        requestWithPage
      );

      console.log(`findTestResults - レスポンス構造:`, {
        hasResponse: !!response,
        responseKeys: Object.keys(response || {}),
        hasScriptTestResultDetails: !!response?.script_test_result_details,
        detailsLength: response?.script_test_result_details?.length || 0,
      });

      if (
        !response.script_test_result_details ||
        response.script_test_result_details.length === 0
      ) {
        console.log(`ページ ${currentPage} のデータはありませんでした`);
        break;
      }

      allResults = allResults.concat(response.script_test_result_details);

      // 次のページがあるかチェック
      if (!response.pagination?.has_next) {
        break;
      }

      currentPage++;
    }

    console.log(`合計 ${allResults.length} 件のテスト結果を取得しました`);

    return {
      script_test_result_details: allResults,
      pagination: {
        has_next: false,
        total: allResults.length,
      },
    };
  }

  // テストケース検索（ページネーション対応）
  async findTestcases(
    request: FindTestcasesRequest
  ): Promise<FindTestcasesResponse> {
    let allTestcases: TestcaseValue[] = [];
    let currentPage = 1;

    console.log("findTestcases request:", request);

    while (true) {
      const requestWithPage = {
        ...request,
        page: currentPage,
      };

      console.log(`ページ ${currentPage} のテストケースを取得中...`);

      const response = await this.post<FindTestcasesResponse>(
        "/testcases/find",
        requestWithPage
      );

      console.log(`findTestcases - レスポンス構造:`, {
        hasResponse: !!response,
        responseKeys: Object.keys(response || {}),
        hasTestcases: !!response?.testcases,
        testcasesLength: response?.testcases?.length || 0,
      });

      if (!response.testcases || response.testcases.length === 0) {
        console.log(`ページ ${currentPage} のテストケースはありませんでした`);
        break;
      }

      allTestcases = allTestcases.concat(response.testcases);

      // 次のページがあるかチェック
      if (!response.pagination?.has_next) {
        break;
      }

      currentPage++;
    }

    console.log(`合計 ${allTestcases.length} 件のテストケースを取得しました`);

    return {
      testcases: allTestcases,
      pagination: {
        cur_page: 1,
        page_size: allTestcases.length,
        total_page: 1,
        has_next: false,
        has_prev: false,
        total_hits: allTestcases.length,
      },
      template: "",
    };
  }

  // GET /testsuites - テストスイート一覧を取得
  async getTestsuites(
    workspaceId: string,
    projectId: string
  ): Promise<GetTestsuitesResponse> {
    const params = {
      workspace_id: workspaceId,
      project_id: projectId,
    };

    console.log("getTestsuites 呼び出し:", params);
    const response = await this.get<GetTestsuitesResponse>(
      "/testsuites",
      params
    );
    console.log(
      `取得したテストスイート数: ${response.testsuites?.length ?? 0}`
    );

    return response;
  }

  // GET /testcases - 特定のテストスイートのテストケース一覧を取得
  async getTestcases(
    workspaceId: string,
    projectId: string,
    testsuiteId: string
  ): Promise<GetTestcasesResponse> {
    const params = {
      workspace_id: workspaceId,
      project_id: projectId,
      testsuite_id: testsuiteId,
    };

    console.log("getTestcases 呼び出し:", params);
    const response = await this.get<GetTestcasesResponse>("/testcases", params);
    console.log(`取得したテストケース数: ${response.testcases?.length ?? 0}`);

    return response;
  }

  // テストターゲット・ユースケース ⇔ テストケースのマッピングを生成
  async getTestcaseMappings(
    workspaceId: string,
    projectId: string
  ): Promise<TestcaseMappings> {
    console.log("getTestcaseMappings開始:", { workspaceId, projectId });

    try {
      // 1. /testcases/find APIで全テストケースを取得（ページネーション対応）
      let allTestcases: TestcaseValue[] = [];
      let currentPage = 1;
      let hasNext = true;

      console.log("/testcases/find APIで全テストケースを取得中...");

      while (hasNext) {
        const request: FindTestcasesRequest = {
          page: currentPage,
          size: 1000,
          workspace_id: workspaceId,
          project_id: projectId,
        };

        console.log(`ページ ${currentPage} を取得中...`);

        const response = await this.post<FindTestcasesResponse>(
          "/testcases/find",
          request
        );

        if (response.testcases && response.testcases.length > 0) {
          allTestcases = allTestcases.concat(response.testcases);
          console.log(`  ${response.testcases.length} 件のテストケースを取得`);
        }

        hasNext = response.pagination?.has_next || false;
        currentPage++;
      }

      console.log(`合計 ${allTestcases.length} 件のテストケースを取得しました`);

      // 2. テストスイート情報を取得してIDから名前を引くMapを作成
      console.log("テストスイート情報を取得中...");
      const testsuitesResponse = await this.getTestsuites(
        workspaceId,
        projectId
      );
      const testsuiteIdToName = new Map<string, string>();
      if (testsuitesResponse.testsuites) {
        testsuitesResponse.testsuites.forEach((testsuite) => {
          testsuiteIdToName.set(testsuite.testsuite_id, testsuite.name);
        });
        console.log(
          `${testsuitesResponse.testsuites.length} 件のテストスイート情報を取得しました`
        );
      }

      // マッピングを格納するMap
      const testTargetToTestcases = new Map<string, TestcaseMapping[]>();
      const usecaseToTestcases = new Map<string, TestcaseMapping[]>();
      const policyToTestcases = new Map<string, TestcaseMapping[]>();

      // 各テストケースを解析
      allTestcases.forEach((testcase, index) => {
        console.log(
          `テストケース ${index + 1}/${allTestcases.length} を解析中: ${
            testcase.name
          }`
        );

        try {
          // value フィールドをパース
          const valueContent: TestcaseValueContent = JSON.parse(
            testcase.value || "{}"
          );

          // 基本情報
          const baseMapping = {
            testcaseId: testcase.testcase_id,
            testcaseName: testcase.name,
            testsuiteId: testcase.testsuite_id,
            testsuiteName:
              testsuiteIdToName.get(testcase.testsuite_id) ||
              testcase.testsuite_id,
            metaId: testcase.meta_id,
            displayOrder: testcase.display_order,
          };

          // 1. テストステップから test_target_id を抽出。
          // 新データは teststep を value に保存しない（読み取り時に導出）ため、
          // 項目単位の紐付けは teststep を持つ既存データに対してのみ構築できる。
          const testSteps = valueContent.teststep || [];
          testSteps.forEach((step, stepIndex) => {
            const testTargets = step.action_detail?.test_targets || [];

            testTargets.forEach((target) => {
              if (!target.test_target_id) {
                return; // test_target_id が無い場合はスキップ
              }

              const mapping: TestcaseMapping = {
                ...baseMapping,
                stepIndex: step.order || stepIndex + 1,
                stepDescription: step.step,
                stepAction: step.action_detail?.step_action?.action,
              };

              const existing =
                testTargetToTestcases.get(target.test_target_id) || [];
              existing.push(mapping);
              testTargetToTestcases.set(target.test_target_id, existing);
            });
          });

          // 2. usecase_policy_results から usecase_id / policy_id を抽出。
          // usecase_id には手順（procedure）の externalId が入り、機能の usecaseId と一致する。
          const usecasePolicyResults = valueContent.usecase_policy_results || [];
          usecasePolicyResults.forEach((result) => {
            if (!result.usecase_id) {
              return;
            }

            const mapping: TestcaseMapping = {
              ...baseMapping,
              usecaseOrder: result.order,
              usecaseReason: result.usecase_reason,
              executionParameters: result.execution_parameters || [],
            };

            const existingUsecase =
              usecaseToTestcases.get(result.usecase_id) || [];
            existingUsecase.push(mapping);
            usecaseToTestcases.set(result.usecase_id, existingUsecase);

            if (result.applied_policy?.policy_id) {
              const existingPolicy =
                policyToTestcases.get(result.applied_policy.policy_id) || [];
              existingPolicy.push(mapping);
              policyToTestcases.set(
                result.applied_policy.policy_id,
                existingPolicy
              );
            }
          });
        } catch (error) {
          console.error(`テストケース ${testcase.name} の解析に失敗:`, error);
        }
      });

      console.log("マッピング生成完了:", {
        testTargetCount: testTargetToTestcases.size,
        usecaseCount: usecaseToTestcases.size,
        policyCount: policyToTestcases.size,
      });

      return {
        testTargetToTestcases,
        usecaseToTestcases,
        policyToTestcases,
      };
    } catch (error) {
      console.error("getTestcaseMappingsでエラーが発生:", error);

      // APIエラーの詳細情報を抽出
      let errorMessage = "不明なエラー";
      if (error instanceof Error) {
        errorMessage = error.message;

        // バックエンドAPIエラーの場合、より詳細な情報を提供
        if (
          errorMessage.includes("attached_urls") ||
          errorMessage.includes("test_case_id")
        ) {
          errorMessage =
            "バックエンドAPIの互換性エラーが発生しました。システム管理者に連絡してください。";
          console.error(
            "詳細: attached_urls フィールドのパースに失敗しました。バックエンドAPIの修正が必要です。"
          );
        }
      }

      throw new Error(
        `テストケースマッピングの取得に失敗しました: ${errorMessage}`
      );
    }
  }

  // ファイルメタデータ取得
  async getFilesMetadata(
    workspaceId: string,
    projectId: string,
    fileIds: string[]
  ): Promise<FileMetadata[]> {
    const requestData = {
      workspace_id: workspaceId,
      project_id: projectId,
      file_ids: fileIds,
    };

    console.log("ファイルメタデータを取得中...", {
      workspaceId,
      projectId,
      fileIds,
    });

    const response = await this.post<FilesMetadataResponse>(
      "/files/find/meta",
      requestData
    );

    console.log("ファイルメタデータを取得しました:", response);
    // files_info配列を返す
    return response.files_info;
  }

  // ファイルダウンロード用一時URL取得（テキストレスポンス用）
  async getFileDownloadUrl(
    workspaceId: string,
    projectId: string,
    fileId: string
  ): Promise<string> {
    const params = {
      workspace_id: workspaceId,
      project_id: projectId,
    };

    console.log("ファイルダウンロードURLを取得中...", {
      workspaceId,
      projectId,
      fileId,
      params,
      useProxy: this.useProxy,
    });

    if (this.useProxy) {
      try {
        console.log("プロキシ経由でファイルダウンロードURL取得中...");

        const response = await fetch(`${this.proxyUrl}/api/proxy`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            method: "GET",
            endpoint: `/files/${fileId}`,
            params,
            data: undefined,
            sstf_base_url: this.sstfBaseUrl,
            // userToken removed - using Auth0 authentication
          }),
        });

        console.log("プロキシレスポンス受信:", {
          status: response.status,
          statusText: response.statusText,
          ok: response.ok,
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error("プロキシエラーレスポンス:", {
            status: response.status,
            statusText: response.statusText,
            errorText,
          });
          throw new Error(
            `Proxy request failed: ${response.status} ${response.statusText}\n${errorText}`
          );
        }

        const result = await response.json();
        const downloadUrl = result.data;

        console.log("ファイルダウンロードURLを取得しました（プロキシ経由）:", {
          downloadUrl:
            downloadUrl.substring(0, 100) +
            (downloadUrl.length > 100 ? "..." : ""),
          fullLength: downloadUrl.length,
        });

        if (
          !downloadUrl ||
          typeof downloadUrl !== "string" ||
          downloadUrl.trim() === ""
        ) {
          throw new Error(
            `有効なダウンロードURLが取得できませんでした: ${downloadUrl}`
          );
        }

        // URLの妥当性をチェック
        const trimmedUrl = downloadUrl.trim();
        if (
          !trimmedUrl.startsWith("http://") &&
          !trimmedUrl.startsWith("https://")
        ) {
          throw new Error(`無効なURL形式です: ${trimmedUrl}`);
        }

        return trimmedUrl;
      } catch (error) {
        console.error("ファイルダウンロードURL取得エラー（プロキシ経由）:", {
          error,
          workspaceId,
          projectId,
          fileId,
          errorMessage: error instanceof Error ? error.message : "不明なエラー",
        });
        throw error;
      }
    } else {
      // 従来の直接アクセス方式
      try {
        // このAPIは文字列を直接返すため、専用の処理が必要
        const searchParams = new URLSearchParams(params).toString();
        const endpoint = `/files/${fileId}?${searchParams}`;

        // APIのbaseURLが/api/v2を含んでいるかチェック
        const baseUrl = this.sstfBaseUrl.endsWith("/api/v2")
          ? this.sstfBaseUrl
          : `${this.sstfBaseUrl}/api/v2`;
        const url = `${baseUrl}${endpoint}`;

        console.log("APIリクエスト実行中:", {
          method: "GET",
          url,
          endpoint,
        });

        const response = await fetch(url, {
          method: "GET",
          headers: {
            // Auth0認証を使用するため、Authorizationヘッダーは不要
          },
        });

        console.log("HTTP Response受信:", {
          status: response.status,
          statusText: response.statusText,
          ok: response.ok,
          url: response.url,
          contentType: response.headers.get("content-type"),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error("HTTP Error Response:", {
            status: response.status,
            statusText: response.statusText,
            errorText,
          });
          throw new Error(
            `API request failed: ${response.status} ${response.statusText}\n${errorText}`
          );
        }

        // テキストレスポンスとして取得
        const downloadUrl = await response.text();

        console.log("ファイルダウンロードURLを取得しました:", {
          downloadUrl:
            downloadUrl.substring(0, 100) +
            (downloadUrl.length > 100 ? "..." : ""),
          fullLength: downloadUrl.length,
        });

        if (
          !downloadUrl ||
          typeof downloadUrl !== "string" ||
          downloadUrl.trim() === ""
        ) {
          throw new Error(
            `有効なダウンロードURLが取得できませんでした: ${downloadUrl}`
          );
        }

        // URLの妥当性をチェック
        const trimmedUrl = downloadUrl.trim();
        if (
          !trimmedUrl.startsWith("http://") &&
          !trimmedUrl.startsWith("https://")
        ) {
          throw new Error(`無効なURL形式です: ${trimmedUrl}`);
        }

        return trimmedUrl;
      } catch (error) {
        console.error("ファイルダウンロードURL取得エラー:", {
          error,
          workspaceId,
          projectId,
          fileId,
          errorMessage: error instanceof Error ? error.message : "不明なエラー",
          errorStack: error instanceof Error ? error.stack : undefined,
        });
        throw error;
      }
    }
  }
  async downloadOperationLog(
    workspaceId: string,
    projectId: string,
    fileId: string
  ): Promise<OperationLog> {
    console.log("操作ログをダウンロード中（ローカルプロキシ経由）...", {
      workspaceId,
      projectId,
      fileId,
    });

    try {
      console.log("プロキシサーバー経由でファイル内容を取得中...");

      // nginx設定に合わせて /files/download パスを使用
      const proxyResponse = await fetch(`${this.proxyUrl}/files/download`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          workspace_id: workspaceId,
          project_id: projectId,
          file_id: fileId,
          sstf_base_url: this.sstfBaseUrl,
          // userToken removed - using Auth0 authentication
        }),
      });

      if (!proxyResponse.ok) {
        const errorText = await proxyResponse.text();
        throw new Error(
          `ローカルプロキシエラー: ${proxyResponse.status} ${proxyResponse.statusText}\n${errorText}`
        );
      }

      const result = await proxyResponse.json();
      console.log("操作ログダウンロード完了（プロキシ経由）");
      return JSON.parse(result.content) as OperationLog;
    } catch (error) {
      console.error("操作ログのダウンロードに失敗:", error);
      throw new Error(
        `操作ログのダウンロードに失敗しました。プロキシサーバーとの通信に問題があります。エラー: ${
          error instanceof Error ? error.message : error
        }`
      );
    }
  }

  // エビデンスファイル用のダウンロードURL取得（表示用）
  async getFileDownloadUrlForDisplay(
    workspaceId: string,
    projectId: string,
    fileId: string
  ): Promise<string> {
    // エビデンスファイル（画像・動画）は署名つきURLを直接返す
    // img src や video src で使用するため、fetchは不要
    return await this.getFileDownloadUrl(workspaceId, projectId, fileId);
  }

  // テスト結果更新（GraphQL mutation）
  async updateTestResults(
    input: UpdateTestResultsInput
  ): Promise<UpdateTestResultsResponse> {
    const query = `
      mutation updateTestResults($updateTestResultsInput: UpdateTestResultsInput!) {
        updateTestResults(updateTestResultsInput: $updateTestResultsInput) {
          scriptTestRunResult {
            id
            testResultStatusId
            metaInfo {
              modifiedAt
              modifiedBy
            }
          }
        }
      }
    `;

    const variables = {
      updateTestResultsInput: input,
    };

    console.log("テスト結果更新GraphQLクエリ実行中...", {
      input,
      variables,
    });

    // GraphQL専用メソッドを使用
    const response = await this.graphqlRequest<{
      updateTestResults: UpdateTestResultsResponse;
    }>(query, variables);

    console.log("テスト結果更新完了:", response);
    return response.updateTestResults;
  }

  // TestTarget更新（GraphQL mutation）
  async updateTestTargets(
    workspaceId: string,
    projectId: string,
    // #884: mainProcedureId/preparationProcedureId を任意で通せる拡張入力を受ける
    // （未指定時は従来の TestTargetUpdateInput と同一挙動）。
    updates: TestTargetUpdateInputWithProcedure[]
  ): Promise<GraphQLTestTarget[]> {
    console.log("TestTarget更新GraphQLクエリ実行中...", {
      workspaceId,
      projectId,
      updates,
    });

    const variables = {
      updateTestTargetsInput: {
        workspaceId,
        projectId,
        testTargets: updates,
      },
    };

    console.log("更新リクエストの詳細:", JSON.stringify(variables, null, 2));

    try {
      const response = await this.graphqlRequest<{
        updateTestTargets: GraphQLTestTarget[];
      }>(UPDATE_TEST_TARGETS_MUTATION, variables);

      console.log("TestTarget更新完了:", response);
      return response.updateTestTargets;
    } catch (error) {
      console.error("TestTarget更新エラー:", error);
      throw new Error(
        `TestTargetの更新に失敗しました: ${
          error instanceof Error ? error.message : "不明なエラー"
        }`
      );
    }
  }

  // 単一TestTarget更新メソッド
  async updateTestTarget(
    workspaceId: string,
    projectId: string,
    testTargetId: string,
    updateData: Partial<TestTargetFormData>
  ): Promise<GraphQLTestTarget> {
    console.log("単一TestTarget更新中...", {
      workspaceId,
      projectId,
      testTargetId,
      updateData,
    });

    // 既存のテストターゲット情報を取得
    const existingTargets = await this.getTestTargets(workspaceId, projectId);
    const existingTarget = existingTargets.find(
      (t) => t.testTargetId === testTargetId
    );

    if (!existingTarget) {
      throw new Error(`TestTarget not found: ${testTargetId}`);
    }

    const effectiveTargetTypeName = (
      updateData.type ?? existingTarget.type.name
    ).toLowerCase();

    // displayOrderのデフォルト値を設定
    let displayOrder = updateData.displayOrder ?? existingTarget.displayOrder;
    if (
      displayOrder === null ||
      displayOrder === undefined ||
      displayOrder === 0
    ) {
      displayOrder = 1.0;
    }

    // 種別変更の有無（paramsを上書きするかマージするかの判定に使用）
    const typeChanged =
      !!updateData.type &&
      updateData.type !== existingTarget.type.name.toLowerCase();

    // 型が変更されている場合のtypeIdの取得
    let typeId = existingTarget.type.typeId;
    if (typeChanged && updateData.type) {
      const typeMapping: Record<string, string> = {
        service: "db0e6706-00fb-5777-bdb5-5a752b6474e3",
        view: "f9553080-aedd-5fe3-8fc8-ace527ca993f",
        item: "bb2d10bf-eaea-5eba-b51d-1e5858546389",
        section: "0a5b3a8b-9247-5350-98b1-c7d84e082ac2",
        overlay: "23152ee4-2333-561e-b945-a2ed1af4882f",
      };
      const newType = updateData.type;
      typeId = typeMapping[newType] || existingTarget.type.typeId;
      console.log(
        `型が変更されました: ${existingTarget.type.name} -> ${updateData.type}, typeId: ${typeId}`
      );
    }

    // paramsの構築（itemDetailsなどを含む）
    let params: Record<string, any> = {};

    // 既存のparamsをベースにする
    try {
      params = JSON.parse(existingTarget.params || "{}");
    } catch (error) {
      console.warn("既存paramsの解析に失敗:", error);
      params = {};
    }

    // 種別変更時は送信されたparamsで上書き、未変更時は既存をベースにマージ。
    // 未変更時のマージでは、送信値が null のキーを「明示的なクリア信号」として扱い、
    // マージ後に当該キーを削除する。これにより FE から partial update のまま
    // 「特定フィールドだけを削除したい」意図を伝達できる（例: マスク種別「なし」）。
    let paramsCleared = false;
    if (updateData.params !== undefined) {
      if (typeChanged) {
        params = { ...updateData.params };
        paramsCleared = Object.keys(updateData.params).length === 0;
        console.log(
          "🔗 種別変更のためparamsを送信値で上書き",
          paramsCleared ? "（空でクリア）" : ""
        );
      } else {
        params = mergeParamsWithClearSignal(
          params,
          updateData.params as Record<string, unknown>
        );
      }
    }

    // statusをparamsに含める（paramsをクリアした場合は追加しない）
    // NOTE: statusがnullの場合は設定しない（初期値は"draft"）
    if (
      !paramsCleared &&
      updateData.status !== undefined &&
      updateData.status !== null
    ) {
      params.status = updateData.status;
    }

    if (effectiveTargetTypeName === "item") {
      stripDeprecatedItemParamKeys(params);
    }

    // paramsをキャメルケースからスネークケースに変換
    const convertedParams = this.camelToSnake(params);

    // 更新データを準備
    // Service種別は親要素を持たないため parentTestTargetId を null にする
    const parentTestTargetId =
      updateData.type === "service"
        ? null
        : (updateData.parentId ?? existingTarget.parentTestTargetId);

    const updateInput = {
      testTargetId,
      name: updateData.name ?? existingTarget.name,
      displayOrder,
      typeId: typeId,
      description: updateData.description ?? existingTarget.description,
      parentTestTargetId,
      params: JSON.stringify(convertedParams),
    } as TestTargetUpdateInput;

    const result = await this.updateTestTargets(workspaceId, projectId, [
      updateInput,
    ]);
    return result[0];
  }

  // TestTarget作成（GraphQL mutation）
  async createTestTargets(
    workspaceId: string,
    projectId: string,
    // #884: mainProcedureId/preparationProcedureId を任意で通せる拡張入力を受ける
    // （未指定時は従来の TestTargetCreateInput と同一挙動）。
    testTargets: TestTargetCreateInputWithProcedure[]
  ): Promise<GraphQLTestTarget[]> {
    console.log("TestTarget作成GraphQLクエリ実行中...", {
      workspaceId,
      projectId,
      testTargets,
    });

    // GraphQLのnon-null displayOrderへ渡せる値に揃えつつ、paramsをスネークケースに変換
    const convertedTestTargets = testTargets.map((target) => {
      const normalizedTarget = {
        ...target,
        displayOrder: normalizeDisplayOrder(target.displayOrder),
      };

      if (target.params) {
        try {
          const params = JSON.parse(target.params);
          const convertedParams = this.camelToSnake(params);
          return {
            ...normalizedTarget,
            params: JSON.stringify(convertedParams),
          };
        } catch (error) {
          console.warn("params変換エラー:", error);
          return normalizedTarget;
        }
      }
      return normalizedTarget;
    });

    const variables = {
      createTestTargetsInput: {
        workspaceId,
        projectId,
        testTargets: convertedTestTargets,
      },
    };

    try {
      const response = await this.graphqlRequest<{
        createTestTargets: GraphQLTestTarget[];
      }>(CREATE_TEST_TARGETS_MUTATION, variables);

      console.log("TestTarget作成完了:", response);
      return response.createTestTargets;
    } catch (error) {
      console.error("TestTarget作成エラー:", error);
      const errorMessage = format_error_message(error);
      throw new Error(`TestTargetの作成に失敗しました: ${errorMessage}`);
    }
  }

  // TestTarget削除
  async deleteTestTargets(
    workspaceId: string,
    projectId: string,
    testTargetIds: string[]
  ): Promise<GraphQLTestTarget[]> {
    console.log("TestTarget削除GraphQLクエリ実行中...", {
      workspaceId,
      projectId,
      testTargetIds,
    });
    const variables = {
      deleteTestTargetsInput: {
        workspaceId,
        projectId,
        testTargetIds,
      },
    };
    try {
      const response = await this.graphqlRequest<{
        deleteTestTargets: GraphQLTestTarget[];
      }>(DELETE_TEST_TARGETS_MUTATION, variables);

      console.log("TestTarget削除完了:", response);
      return response.deleteTestTargets;
    } catch (error) {
      console.error("TestTarget削除エラー:", error);
      throw new Error(
        `TestTargetの削除に失敗しました: ${
          error instanceof Error ? error.message : "不明なエラー"
        }`
      );
    }
  }

  // 単一のTestTarget削除
  async deleteTestTarget(
    workspaceId: string,
    projectId: string,
    testTargetId: string
  ): Promise<GraphQLTestTarget[]> {
    return this.deleteTestTargets(workspaceId, projectId, [testTargetId]);
  }

  // Issue検索（/issues/find エンドポイント）
  async findIssues(
    workspaceId: string,
    projectId: string,
    usecaseId: string,
    page: number = 0,
    size: number = 100
  ): Promise<IssuePagingResponse> {
    // 新モデル: Issue は旧 usecase_id ではなく procedure（feature）識別子で紐付く。
    // 引数 usecaseId は呼び出し側の後継識別子（procedure/feature id）をそのまま受ける。
    // TODO(#884): Issue value 上の実キー名が確定したら "value.procedure_id" を正キーへ揃える。
    const request: FindIssuesInput = {
      workspace_id: workspaceId,
      project_id: projectId,
      query: {
        and: [
          {
            key: "value.procedure_id",
            type: "keyword",
            value: usecaseId,
          },
        ],
      },
      page,
      size,
    };

    try {
      console.log("🔍 Issue検索開始:", { workspaceId, projectId, usecaseId });
      const response = await this.post<IssuePagingResponse>(
        "/issues/find",
        request
      );
      console.log("✅ Issue検索完了:", response);
      return response;
    } catch (error) {
      console.error("❌ Issue検索エラー:", error);
      throw new Error(
        `Issueの検索に失敗しました: ${
          error instanceof Error ? error.message : "不明なエラー"
        }`
      );
    }
  }
}

// テスト結果と操作ログのマッピング型
export interface TestResultWithOperationLog {
  test_run_id: string;
  test_run_name: string;
  test_case_id: string;
  test_case_name: string;
  result_id: string;
  test_result_status_id: string;
  modified_at: string;
  operation_logs: OperationLogStep[];
}

export interface OperationLogStep {
  test_target_id: string;
  item_name: string;
  action: string;
  result: boolean;
  start_time: number;
  end_time: number;
  operation_type: string;
}

// GET /testsuites の型定義
export interface GetTestsuitesResponse {
  testsuites: Testsuite[];
}

export interface Testsuite {
  workspace_id: string;
  project_id: string;
  testsuite_id: string;
  meta_id: string;
  meta_id_sequence: number;
  name: string;
  value?: string;
  created_at: string;
  created_by: string;
  modified_at: string;
  modified_by: string;
}

// GET /testcases の型定義
export interface GetTestcasesResponse {
  testcases: TestcaseValue[];
}

// テストケース検索関連の型定義（旧API - 非推奨）
export interface FindTestcasesRequest {
  page: number;
  size: number;
  workspace_id: string;
  project_id: string;
  query?: {
    or?: Array<{ key: string; type: string; value: string }>;
    and?: Array<{ key: string; type: string; value: string }>;
  };
  sort_orders?: Array<any>;
}

export interface FindTestcasesResponse {
  testcases: TestcaseValue[];
  pagination: {
    cur_page: number;
    page_size: number;
    total_page: number;
    has_next: boolean;
    has_prev: boolean;
    total_hits: number;
  };
  template: string;
}

export interface TestcaseValue {
  workspace_id: string;
  project_id: string;
  testsuite_id: string;
  testcase_id: string;
  meta_id: string;
  meta_id_sequence: number;
  display_order: number;
  name: string;
  estimated_time_in_seconds: number;
  value: string; // JSON文字列
  created_at: string;
  created_by: string;
  modified_at: string;
  modified_by: string;
  managed_file_list?: ManagedFile[];
  attached_url_list?: any[];
}

// testcase.value の内部構造。
// usecase_policy_results[].usecase_id には手順（procedure）の externalId が入る。
// teststep は新データでは保存されない（読み取り時に導出）ため、既存データでのみ存在しうる。
export interface TestcaseValueContent {
  id?: string;
  title?: string;
  note?: string;
  teststep?: TestStep[];
  usecase_policy_results?: UsecasePolicyResult[];
  prerequisite?: string;
  primary_item?: string;
  secondary_item?: string;
  tertiary_item?: string;
  expected_result?: string;
  verification_procedure?: string;
  test_case_ai_status?: string;
}

export interface UsecasePolicyResult {
  order?: number;
  usecase_id: string;
  test_target_id?: string;
  applied_policy?: { policy_id: string } | null;
  usecase_reason?: string;
  execution_parameters?: Array<{ name: string; value: string }>;
  error?: string | null;
}

export interface TestStep {
  id: string;
  step: string;
  order: number;
  expect: string;
  reason: string;
  is_error: boolean;
  action_detail: {
    step_action: {
      action: string;
      parameters: Array<{ key: string; value: string }>;
    };
    test_targets: Array<{
      id: string;
      name: string;
      test_target_id: string;
    }>;
  };
}

// マッピング結果型
export interface TestcaseMapping {
  testcaseId: string;
  testcaseName: string;
  testsuiteId: string;
  testsuiteName: string;
  metaId: string;
  displayOrder: number;
  // テストステップ関連（テストターゲット用）
  stepIndex?: number;
  stepDescription?: string;
  stepAction?: string;
  // ユースケース関連
  usecaseOrder?: number;
  usecaseReason?: string;
  executionParameters?: Array<{
    name: string;
    value: string;
  }>;
}

// 全体のマッピング結果
export interface TestcaseMappings {
  // test_target_id -> テストケース情報の配列
  testTargetToTestcases: Map<string, TestcaseMapping[]>;
  // usecase_id -> テストケース情報の配列
  usecaseToTestcases: Map<string, TestcaseMapping[]>;
  // policy_id -> テストケース情報の配列（将来用）
  policyToTestcases: Map<string, TestcaseMapping[]>;
}

// データ型定義（Pythonのapp.pyから抽出）
export interface Workspace {
  workspace_id: string;
  name: string;
  slug: string;
}

// GraphQLから取得するTestTargetの型定義
export interface GraphQLTestTargetMetaInfo {
  createdAt?: string;
  createdBy?: string;
  modifiedAt?: string;
  modifiedBy?: string;
}

export interface GraphQLTestTarget {
  testTargetId: string;
  name: string;
  displayOrder: number;
  workspaceId: string;
  projectId: string;
  type: {
    name: string;
    typeId: string;
  };
  description?: string;
  parentTestTargetId?: string;
  /** @deprecated SSTF 側で value に統合（同一カラム）。新規コードは value を使う */
  params?: string; // JSON文字列
  /** JSON文字列。トップレベル=従来params、poc サブキー=poc固有。#884 で追加 */
  value?: string;
  /** 紐づく前提手順の SSTF UUID（GraphQL preparationProcedure.id の平坦化）。#884 で追加 */
  preparationProcedure?: { id: string } | null;
  /** 紐づくメイン手順の SSTF UUID（GraphQL mainProcedure.id の平坦化）。feature 識別に使う。#884 で追加 */
  mainProcedure?: { id: string } | null;
  metaInfo?: GraphQLTestTargetMetaInfo;
}

/** SSTF Procedure（手順）の GraphQL 応答形。value は JSON 文字列（フラット blob）。#884 で追加 */
export interface GraphQLProcedure {
  id: string;
  workspaceId: string;
  projectId: string;
  name: string;
  displayOrder: number;
  inheritFromParent: boolean;
  value?: string | null;
  metaInfo?: {
    createdAt?: string;
    modifiedAt?: string;
  };
}

/** createProcedures の入力（単体/ネスト共用）。#884 */
export interface CreateProcedureInput {
  id?: string;
  name: string;
  displayOrder?: number;
  inheritFromParent?: boolean;
  value?: string;
}

/** updateProcedures の入力。SSTF の update は完全置換のため name/displayOrder/inheritFromParent 必須。#884 */
export interface UpdateProcedureInput {
  id: string;
  name: string;
  displayOrder: number;
  inheritFromParent: boolean;
  value?: string;
}

/**
 * feature ターゲット（main procedure 付き）作成用の拡張入力。#884
 * SSTF CreateTestTargetInput は mainProcedureId/preparationProcedureId: ID を受ける。
 * 未指定時は従来通り（procedure リンク無し）。
 */
export interface TestTargetCreateInputWithProcedure
  extends TestTargetCreateInput {
  // 作成時は SSTF の CreateTestTargetInput が mainProcedureId(ID リンク)を受け付けず、
  // ネストした mainProcedure(CreateProcedureInput)で procedure を同時作成する。#884
  mainProcedure?: CreateProcedureInput;
  preparationProcedure?: CreateProcedureInput;
}

/**
 * feature ターゲット（main procedure 付き）更新用の拡張入力。#884
 * 未指定時は従来通り（procedure リンクを変更しない）。
 */
export interface TestTargetUpdateInputWithProcedure
  extends TestTargetUpdateInput {
  mainProcedureId?: string | null;
  preparationProcedureId?: string | null;
}

export interface Project {
  project_id: string;
  name: string;
  slug: string;
}

export interface TestRun {
  test_run_id: string;
  name: string;
  workspace_id: string;
  project_id: string;
}

export interface FindTestRunsRequest {
  page: number;
  size: number;
  workspace_id: string;
  project_id: string;
}

export interface TestRunWithSummary {
  test_run: {
    workspace_id: string;
    project_id: string;
    test_run_id: string;
    meta_id: string;
    meta_id_sequence: number;
    name: string;
    test_run_status_id: number;
    value: string;
    test_run_matrix: {
      testsuite_id: string;
      execution_environment_ids: string[] | null;
    };
    created_at: string;
    created_by: string;
    modified_at: string;
    modified_by?: string;
  };
  test_run_summary: {
    total_testcases: number;
    total_test_run_matrix: number;
    total_execution_environments: number;
    script_test_run_issue_summaries: Array<{
      issue_status_id: string;
      issue_status_count: number;
    }>;
    script_test_run_result_summaries: Array<{
      test_result_status_id: string;
      test_result_status_count: number;
    }>;
    total_estimated_time_in_seconds: number;
    estimated_time_available_matrix: number;
  };
}

export interface FindTestRunsResponse {
  test_run_with_summaries: TestRunWithSummary[];
  template: string;
  pagination: {
    cur_page: number;
    page_size: number;
    total_page: number;
    has_next: boolean;
    has_prev: boolean;
    total_hits: number;
  };
}

export interface Template {
  template: string; // JSON文字列
}

export interface FindTestResultsRequest {
  page: number;
  size: number;
  workspace_id: string;
  project_id: string;
  query?: {
    or?: Array<{
      key: string;
      type: string;
      value: string;
    }>;
    and?: Array<{
      key: string;
      type: string;
      value: string;
    }>;
  };
}

export interface FindTestResultsResponse {
  script_test_result_details: ScriptTestResultDetail[];
  pagination: {
    has_next: boolean;
    total: number;
  };
}

export interface ScriptTestResultDetail {
  test_run: {
    test_run_id: string;
    meta_id: string;
    name: string;
    workspace_id: string;
    project_id: string;
  };
  test_run_test_suite: {
    testsuite_id: string;
    meta_id: string;
    name: string;
  };
  test_run_test_case: {
    testcase_id: string;
    meta_id: string;
    name: string;
    value: string; // JSON文字列
    display_order: number;
  };
  test_run_execution_environment: {
    execution_environment_id: string;
    name: string;
  };
  script_test_run_result: {
    result_id: string;
    script_test_run_matrix_id: string;
    test_result_status_id: string;
    modified_at: string;
    comment: string;
    value: string; // JSON文字列
    actual_time_in_seconds?: number;
    managed_file_list?: ManagedFile[];
  };
}

export interface ManagedFile {
  managed_file_id: string;
  file_name: string;
}

export interface FileMetadata {
  file_id: string;
  file_name: string;
  display_name: string;
  status: string;
  mime_type: string;
  tags: string | null;
  category: string;
  file_size: number;
  desc: string;
  owner_id: string;
  created_at: string;
  created_by: string;
  modified_at: string;
  modified_by: string;
}

export interface FilesMetadataResponse {
  project_id: string;
  files_info: FileMetadata[];
}

// 操作ログのデータ型定義
export interface OperationLog {
  trace_id: string;
  exec_mode: string;
  steps: OperationStep[];
  videos: OperationVideo[];
}

export interface OperationStep {
  result: boolean;
  action: string;
  test_target_id: string;
  params: Record<string, any>;
  start_time: number;
  end_time: number;
  analyze_exec: boolean;
  item: {
    name: string;
    description: string;
  };
  evidence: string | null;
  operation_type: string;
}

export interface OperationVideo {
  file: string;
  start_time: number;
}

// 自動実行統計レスポンスの型定義
export interface AutomationStatsResponse {
  test_target_stats: TestTargetAutomationStats[];
}

export interface TestTargetAutomationStats {
  test_target_id: string;
  total_testcases: number;
  testcase_names: string[];
}

// テスト結果更新用の型定義
export interface UpdateTestResultsInput {
  workspaceId: string;
  projectId: string;
  testRunId: string;
  testResultInputs: TestResultInput[];
}

export interface TestResultInput {
  actualTimeInSeconds?: number;
  resultId: string;
  scriptTestRunMatrixId: string;
  testResultStatusId: string;
  value?: string;
}

// GraphQLレスポンス用の型定義（シンプル版）
export interface UpdateTestResultsResponse {
  scriptTestRunResult: {
    id: string;
    testResultStatusId: string;
    metaInfo: {
      modifiedAt: string;
      modifiedBy: string;
    };
  };
}

// UTC文字列をJSTに変換（Pythonから移植）
export function convertToJST(utcStr: string | number): string {
  let date: Date;

  if (typeof utcStr === "number") {
    // 数値の場合はそのままタイムスタンプとして扱う
    date = new Date(utcStr);
  } else if (typeof utcStr === "string") {
    // 文字列の場合
    if (/^\d+$/.test(utcStr)) {
      // 数値文字列の場合はタイムスタンプとして扱う
      date = new Date(parseInt(utcStr, 10));
    } else {
      // ISO文字列の場合
      date = new Date(utcStr.replace("Z", "+00:00"));
    }
  } else {
    return "Invalid Date";
  }

  if (isNaN(date.getTime())) {
    return "Invalid Date";
  }

  return date.toLocaleString("ja-JP", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

// テンプレートからステータス表示名を取得（Pythonから移植）
export function getStatusDisplayName(
  template: Template,
  statusId: string
): string {
  try {
    const templateData = JSON.parse(template.template);
    const statusField = templateData.fields.find(
      (field: any) => field.id === "test_result_status"
    );

    if (statusField) {
      const option = statusField.data.options.find(
        (opt: any) => opt.value === statusId
      );
      if (option) {
        return option.display_name.ja;
      }
    }
    return "未実施";
  } catch (error) {
    console.error("ステータス表示名の取得に失敗:", error);
    return "未実施";
  }
}

// テンプレートからプロジェクト固有のステータスIDを取得
export interface ProjectStatusIds {
  testResultStatus: {
    UNTESTED: string;
    OK: string;
    NG_TO_OK: string;
    NG: string;
    BLOCKING: string;
    PENDING: string;
    TEST_CASE_MODIFICATION: string;
    NA: string;
    NT: string;
    AI_OK: string;
    AI_RESULT_NG: string;
    AI_PROCEDURE_NG: string;
    AI_OK_NEED_REVIEW: string;
    AI_RESULT_NG_NEED_REVIEW: string;
    AI_PROCEDURE_NG_NEED_REVIEW: string;
  };
  aiStatus: {
    NOT_REQUESTED: string;
    TEST_RUN: string;
    ANALYSIS_OF_RESULTS: string;
    ANALYSIS_OF_DEFECTS: string;
    REQUESTED: string;
    QUEUED: string;
    IN_PROGRESS: string;
    COMPLETED: string;
    TERMINATED: string;
    AWAITING_RESTART: string;
    ERROR: string;
  };
}

export function extractProjectStatusIds(template: Template): ProjectStatusIds {
  console.log("=== プロジェクトステータスID抽出開始 ===");

  try {
    const templateData = JSON.parse(template.template);
    console.log("テンプレートデータ解析成功");

    // test_result_statusフィールドからステータスIDを抽出
    const statusField = templateData.fields.find(
      (field: any) => field.id === "test_result_status"
    );
    console.log("test_result_statusフィールド:", statusField);

    // test_result_ai_statusフィールドからAIステータスIDを抽出
    const aiStatusField = templateData.attributes?.find(
      (attr: any) => attr.id === "test_result_ai_status"
    );
    console.log("test_result_ai_statusフィールド:", aiStatusField);

    const testResultStatus: any = {};
    const aiStatus: any = {};

    // テスト結果ステータスIDを抽出
    if (statusField?.data?.options) {
      statusField.data.options.forEach((option: any) => {
        const displayName =
          option.display_name?.en || option.display_name?.ja || "";
        const globalName = option.global_master_name;

        // display_nameやglobal_master_nameに基づいてマッピング
        if (displayName === "Untested" || globalName === "untested") {
          testResultStatus.UNTESTED = option.value;
          console.log("✅ UNTESTED マッピング:", option.value);
        } else if (displayName === "OK") {
          testResultStatus.OK = option.value;
          console.log("✅ OK マッピング:", option.value);
        } else if (displayName === "NG→OK") {
          testResultStatus.NG_TO_OK = option.value;
          console.log("✅ NG_TO_OK マッピング:", option.value);
        } else if (displayName === "NG") {
          testResultStatus.NG = option.value;
          console.log("✅ NG マッピング:", option.value);
        } else if (displayName === "Blocking" || displayName === "BK") {
          testResultStatus.BLOCKING = option.value;
          console.log("✅ BLOCKING マッピング:", option.value);
        } else if (displayName === "Pending" || displayName === "保留") {
          testResultStatus.PENDING = option.value;
          console.log("✅ PENDING マッピング:", option.value);
        } else if (
          displayName === "TestCaseModification" ||
          displayName === "要テストケース修正"
        ) {
          testResultStatus.TEST_CASE_MODIFICATION = option.value;
          console.log("✅ TEST_CASE_MODIFICATION マッピング:", option.value);
        } else if (displayName === "N/A") {
          testResultStatus.NA = option.value;
          console.log("✅ NA マッピング:", option.value);
        } else if (displayName === "N/T") {
          testResultStatus.NT = option.value;
          console.log("✅ NT マッピング:", option.value);
        } else if (displayName === "[AI]OK") {
          testResultStatus.AI_OK = option.value;
          console.log("✅ AI_OK マッピング:", option.value);
        } else if (
          displayName === "[AI]Result NG" ||
          displayName === "[AI]結果NG"
        ) {
          testResultStatus.AI_RESULT_NG = option.value;
          console.log("✅ AI_RESULT_NG マッピング:", option.value);
        } else if (
          displayName === "[AI]Procedure NG" ||
          displayName === "[AI]手順NG"
        ) {
          testResultStatus.AI_PROCEDURE_NG = option.value;
          console.log("✅ AI_PROCEDURE_NG マッピング:", option.value);
        } else if (
          displayName === "[AI]OK(Need Review)" ||
          displayName === "[AI]OK(要確認)"
        ) {
          testResultStatus.AI_OK_NEED_REVIEW = option.value;
          console.log("✅ AI_OK_NEED_REVIEW マッピング:", option.value);
        } else if (
          displayName === "[AI]Result NG(Need Review)" ||
          displayName === "[AI]結果NG(要確認)"
        ) {
          testResultStatus.AI_RESULT_NG_NEED_REVIEW = option.value;
          console.log("✅ AI_RESULT_NG_NEED_REVIEW マッピング:", option.value);
        } else if (
          displayName === "[AI]Procedure NG(Need Review)" ||
          displayName === "[AI]手順NG(要確認)"
        ) {
          testResultStatus.AI_PROCEDURE_NG_NEED_REVIEW = option.value;
          console.log(
            "✅ AI_PROCEDURE_NG_NEED_REVIEW マッピング:",
            option.value
          );
        } else {
          console.log("⚠️ マッピングされていないオプション:", {
            displayName,
            globalName,
            value: option.value,
          });
        }
      });
    } else {
      console.log("⚠️ ステータスオプションが見つかりません");
    }

    // AIステータスIDを抽出
    if (aiStatusField?.data?.options) {
      console.log(
        "AIステータスオプション数:",
        aiStatusField.data.options.length
      );
      aiStatusField.data.options.forEach((option: any) => {
        const globalName = option.global_master_name;

        if (globalName === "not_requested") {
          aiStatus.NOT_REQUESTED = option.value;
        } else if (globalName === "test_run") {
          aiStatus.TEST_RUN = option.value;
        } else if (globalName === "analysis_of_results") {
          aiStatus.ANALYSIS_OF_RESULTS = option.value;
        } else if (globalName === "analysis_of_defects") {
          aiStatus.ANALYSIS_OF_DEFECTS = option.value;
        } else if (globalName === "requested") {
          aiStatus.REQUESTED = option.value;
        } else if (globalName === "queued") {
          aiStatus.QUEUED = option.value;
        } else if (globalName === "in_progress") {
          aiStatus.IN_PROGRESS = option.value;
        } else if (globalName === "completed") {
          aiStatus.COMPLETED = option.value;
        } else if (globalName === "terminated") {
          aiStatus.TERMINATED = option.value;
        } else if (globalName === "awaiting_restart") {
          aiStatus.AWAITING_RESTART = option.value;
        } else if (globalName === "error") {
          aiStatus.ERROR = option.value;
        }
      });
    }

    const result = {
      testResultStatus,
      aiStatus,
    };

    console.log("抽出されたステータスIDs:", result);
    console.log("=== プロジェクトステータスID抽出完了 ===");

    return result;
  } catch (error) {
    console.error("❌ プロジェクトステータスIDの抽出に失敗:", error);
    throw new Error(
      `プロジェクトテンプレートからステータスIDを抽出できませんでした。テンプレートの構造を確認してください: ${
        error instanceof Error ? error.message : error
      }`
    );
  }
}

// テストステップ用のステータスIDを抽出
export interface TestStepStatusIds {
  OK: string;
  NG: string;
  SUCCESS_IDS: string[]; // 成功と判定すべきすべてのステータスID
  WARNING_IDS: string[]; // 警告（要確認）と判定すべきすべてのステータスID
}

// TestTargetType型定義
export interface TestTargetType {
  typeId: string;
  name: string;
  testTargetValueSchema?: string;
  __typename?: string;
}

export function extractTestStepStatusIds(
  template: Template
): TestStepStatusIds {
  console.log("=== テストステップステータスID抽出開始 ===");
  console.log("テンプレート全体:", template);

  try {
    const templateData = JSON.parse(template.template);
    console.log("テストステップテンプレートデータ解析成功");
    console.log("テンプレートデータ全体:", templateData);
    console.log("fieldsの数:", templateData.fields?.length);

    // 全てのフィールドを出力して構造を確認
    if (templateData.fields) {
      console.log("=== 全フィールド一覧 ===");
      templateData.fields.forEach((field: any, index: number) => {
        console.log(`フィールド ${index + 1}:`, {
          id: field.id,
          type: field.type,
          label: field.label,
          hasData: !!field.data,
          hasOptions: !!field.data?.options,
          optionsCount: field.data?.options?.length || 0,
        });
      });
    }

    // test_result_statusフィールドを探す（テストステップも同じテンプレートを使用）
    const statusField = templateData.fields.find(
      (field: any) => field.id === "test_result_status"
    );
    console.log("test_result_statusフィールド:", statusField);

    // 見つからない場合、類似のフィールドを探す
    if (!statusField) {
      console.log(
        "test_result_statusが見つかりません。類似フィールドを検索中..."
      );
      const statusRelatedFields = templateData.fields.filter(
        (field: any) =>
          field.id.includes("status") || field.id.includes("result")
      );
      console.log("ステータス関連フィールド:", statusRelatedFields);
    }

    const testStepStatus: any = {
      OK: "",
      NG: "",
      SUCCESS_IDS: [], // 成功と判定すべきすべてのステータスID
      WARNING_IDS: [], // 警告（要確認）と判定すべきすべてのステータスID
    };

    // テストステップ結果ステータスIDを抽出
    if (statusField?.data?.options) {
      console.log(
        "テストステップステータスオプション数:",
        statusField.data.options.length
      );
      console.log("=== 全オプション詳細 ===");

      const successIds: string[] = [];
      const warningIds: string[] = [];

      statusField.data.options.forEach((option: any) => {
        const displayNameEn = option.display_name?.en || "";
        const displayNameJa = option.display_name?.ja || "";
        const testResultType = option.test_result_type?.value;

        console.log(`オプション:`, {
          value: option.value,
          displayNameEn,
          displayNameJa,
          testResultType,
          fullOption: option,
        });

        // test_result_type の値に基づいて成功/失敗を判定
        // "1" = 成功系（OK、NG→OK、[AI]OKなど）
        if (testResultType === "1") {
          successIds.push(option.value);
          console.log(
            "✅ 成功ステータスとして追加:",
            option.value,
            displayNameJa
          );
        }

        // "5" = 警告系（要確認）
        if (testResultType === "5") {
          warningIds.push(option.value);
          console.log(
            "⚠️ 警告ステータスとして追加:",
            option.value,
            displayNameJa
          );
        }

        // 個別のマッピング（後方互換性のため）
        if (displayNameEn === "OK" && displayNameJa === "OK") {
          testStepStatus.OK = option.value;
          console.log("✅ テストステップ OK マッピング:", option.value);
        } else if (displayNameEn === "NG" && displayNameJa === "NG") {
          testStepStatus.NG = option.value;
          console.log("✅ テストステップ NG マッピング:", option.value);
        }
      });

      testStepStatus.SUCCESS_IDS = successIds;
      testStepStatus.WARNING_IDS = warningIds;
      console.log("✅ 成功ステータスIDs一覧:", successIds);
      console.log("✅ 警告ステータスIDs一覧:", warningIds);
    } else {
      console.log("⚠️ テストステップステータスオプションが見つかりません");
      console.log("statusField:", statusField);
      console.log("statusField.data:", statusField?.data);
    }

    const result = testStepStatus;

    console.log("抽出されたテストステップステータスIDs:", result);
    console.log("=== テストステップステータスID抽出完了 ===");

    return result;
  } catch (error) {
    console.error("❌ テストステップステータスIDの抽出に失敗:", error);
    throw new Error(
      `テストステップテンプレートからステータスIDを抽出できませんでした。テンプレートの構造を確認してください: ${
        error instanceof Error ? error.message : error
      }`
    );
  }
}

/**
 * テストケースのユースケース・ポリシーを一括更新する
 * @param apiClient APIクライアントインスタンス
 * @param payload 更新用のペイロード
 * @returns 更新結果
 */
export async function bulkUpdateTestCasesUsecase(
  apiClient: SSTFApiClient,
  payload: BulkUpdateTestCasesUsecasePayload
): Promise<any> {
  try {
    console.log("=== テストケースのユースケース一括更新開始 ===");
    console.log("Payload:", payload);

    // /testcases エンドポイントにPUTリクエストを送信
    const result = await apiClient.put("/testcases", payload);

    console.log("テストケース一括更新成功:", result);
    console.log("=== テストケースのユースケース一括更新完了 ===");

    return result;
  } catch (error) {
    console.error("❌ テストケースのユースケース一括更新に失敗:", error);
    throw new Error(
      `テストケースのユースケース一括更新に失敗しました: ${
        error instanceof Error ? error.message : error
      }`
    );
  }
}

/**
 * テストケースからユースケースの紐付けを一括削除する
 * @param apiClient APIクライアントインスタンス
 * @param payload 削除用のペイロード
 * @returns 削除結果
 */
export async function bulkDeleteTestCasesUsecase(
  _apiClient: SSTFApiClient,
  payload: BulkDeleteTestCasesUsecasePayload
): Promise<any> {
  // Phase B（#884）: 旧 usecase_policy_results を合成して /testcases へ PUT する経路を停止する。
  // この経路はテストケース value を旧 shape で再構成して書き戻すため、新モデル（procedures /
  // procedure_id / test_condition_id）と衝突しデータを壊す。新契約での手順再連携は別 issue で設計する。
  // ここでは実 PUT を行わず、deprecation を警告して成功相当（no-op）を返す。
  // TODO(#884): procedures ベースの紐付け解除 API が用意されたら差し替える。
  console.warn(
    "[deprecated:#884] bulkDeleteTestCasesUsecase は無効化されました（旧 usecase_policy_results への" +
      " 書き戻しを停止）。procedures ベースの再連携は別 issue で対応します。",
    {
      workspace_id: payload.workspace_id,
      project_id: payload.project_id,
      testsuite_id: payload.testsuite_id,
      usecase_id: payload.usecase_id,
      testcase_ids: payload.testcase_ids,
    }
  );

  return {
    success: true,
    deprecated: true,
    updated: 0,
    testcases: [],
  };
}

/**
 * サーバーサイドのバルク更新APIを呼び出す
 * @param payload バルク更新用のペイロード
 * @returns 更新結果
 */
export async function callBulkUpdateApi(payload: {
  workspace_id: string;
  project_id: string;
  testcase_ids: string[];
  update_type: "usecase" | "parameter" | "ai_status";
  source_usecase_id?: string;
  target_usecase_id?: string;
  policy_id?: string;
  parameter_name?: string;
  parameter_value?: string;
  ai_status_id?: string;
  sstf_base_url: string;
  // user_token removed - using Auth0 authentication
}): Promise<any> {
  try {
    console.log("=== サーバーサイドバルク更新API呼び出し開始 ===");
    console.log("Payload:", payload);

    // サーバーサイドのバルク更新エンドポイントを呼び出し
    const response = await fetch("/api/bulk-update-testcases", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.message || `HTTP ${response.status}: ${response.statusText}`
      );
    }

    const result = await response.json();
    console.log("バルク更新成功:", result);
    console.log("=== サーバーサイドバルク更新API呼び出し完了 ===");

    return result;
  } catch (error) {
    console.error("❌ サーバーサイドバルク更新APIの呼び出しに失敗:", error);
    throw new Error(
      `バルク更新に失敗しました: ${
        error instanceof Error ? error.message : error
      }`
    );
  }
}

/**
 * サーバーサイドのバルク削除APIを呼び出す
 * @param payload バルク削除用のペイロード
 * @returns 削除結果
 */
export async function callBulkDeleteApi(payload: {
  workspace_id: string;
  project_id: string;
  testcase_ids: string[];
  usecase_id: string;
  sstf_base_url: string;
  // user_token removed - using Auth0 authentication
}): Promise<any> {
  try {
    console.log("=== サーバーサイドバルク削除API呼び出し開始 ===");
    console.log("Payload:", payload);

    // サーバーサイドのバルク削除エンドポイントを呼び出し
    const response = await fetch("/api/bulk-delete-testcases-usecase", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.message || `HTTP ${response.status}: ${response.statusText}`
      );
    }

    const result = await response.json();
    console.log("バルク削除成功:", result);
    console.log("=== サーバーサイドバルク削除API呼び出し完了 ===");

    return result;
  } catch (error) {
    console.error("❌ サーバーサイドバルク削除APIの呼び出しに失敗:", error);
    throw new Error(
      `バルク削除に失敗しました: ${
        error instanceof Error ? error.message : error
      }`
    );
  }
}
